import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router'

import { t } from '../../copy'
import { useAccountMembership, useMe, useProjects } from '../../app/queries'
import { endpointStatus, type ConversationMode } from './backend-contract'
import { LaunchPage } from './launch'
import { resolveRecentProjects } from './launch-state'
import { buildCheckoutRegistration, mayProceed, type FolderVerdict } from './folder'
import { pickLocalFolder } from './folder-adapter'
import { detectSurface } from './platform'
import { groupRailConversations } from './rail'
import {
  useLuluConversations,
  useProjectConversations,
  useProjectInitStatus,
  createLuluConversation,
  createProjectNamed,
  registerCheckout,
} from './queries'
import {
  clearProvisioning,
  conversationCreateKey,
  nextProvisioningStep,
  projectCreateKey,
  readProvisioning,
  writeProvisioning,
  type ProvisioningRecord,
} from './provisioning-flow'
import { fromWireInitStatus, isReadableInitStatus, stageRows } from './provisioning'
import {
  conversationCreateKeys,
  releaseCreateConversationKey,
  type ConversationCreateIntent,
} from './conversation-create-key'
import { HiddenAttachmentInput } from './attachment-input'
import { markSeen, unreadIds } from './unread'
import { useConversationWorkbench } from './use-conversation-workbench'
import { useSidecarLayout } from './use-sidecar-layout'
import { LuluView } from './view'

/**
 * Lulu 工作台的接线层。**这一屏独有的**东西在这儿：会话列表与分组、路由、
 * 启动器、新建项目的三阶段、未读点。呈现全在 `view.tsx`。
 *
 * **与这一条会话有关的一切**（取数、投影、每一个动作）在
 * `use-conversation-workbench.ts`——项目页里的对话用的是同一份。
 * 2026-09-08 之前两处各接一份，抄的那天一样，之后每修一处另一处就少一处
 * （见那个文件顶上那段）。Sidecar 的开合同理在 `use-sidecar-layout.ts`。
 */

/**
 * 把这一屏钉在外壳留给它的那块空间里，自己在内部滚。
 *
 * # 为什么是 absolute inset-0，而不是 h-[calc(100vh-46px)]
 *
 * 外壳是 `min-h-screen` + `flex-1` 的 main。内容一高，main 就跟着长，
 * **整页开始滚**——而这一屏要的是聊天列自己滚、输入框钉在底下不动。
 *
 * 减去一个 46px 能解决今天的问题，代价是把外壳顶栏的高度抄进了这一屏。
 * 外壳在项目路由下还会多一条 34px 的页签栏，那时这个数就错了，
 * **而表现是底部被裁掉一条，不是报错**。
 *
 * 绝对定位不用知道上面有几条栏：唯一的子节点不参与高度计算，
 * 于是 main 的内容高是 0，`min-h-screen` 把外壳收成正好一屏，
 * 剩下多少这一屏就占多少。外壳哪天再加一条栏，这里自动跟着变。
 */
function LuluFrame({
  measureRef,
  children,
}: {
  measureRef: (node: HTMLElement | null) => void
  children: React.ReactNode
}) {
  return (
    <div className="relative min-h-0 flex-1">
      <div ref={measureRef} className="absolute inset-0 flex">
        {children}
      </div>
    </div>
  )
}

export function LuluScreen() {
  const navigate = useNavigate()
  const creatorUserId = useMe().data?.id
  const membership = useAccountMembership()
  const canStartConversation = membership.data?.capabilities.includes('operate') ?? false
  const canCreateProject = membership.data?.capabilities.includes('project.manage') ?? false
  const params = useParams({ strict: false }) as { conversationId?: string }

  const conversations = useLuluConversations()
  // 左栏要按项目分组，而账户级那条**只列不挂项目的**（契约逐字如此）。
  // 项目会话得逐个项目问——见 useProjectConversations 那段。
  const railProjects = useProjects()
  const projectIds = useMemo(
    () => (railProjects.data?.items ?? []).map((p) => p.id),
    [railProjects.data?.items],
  )
  const projectConversations = useProjectConversations(projectIds)
  const projectConversationData = projectConversations.map((q) => q.data)
  const list = useMemo(() => {
    const rows = [
      ...(conversations.data?.conversations ?? []),
      ...projectConversationData.flatMap((d) => d?.conversations ?? []),
    ]
    // **按 id 去重。** 两条接口理论上不重叠，而「理论上不重叠」这句话
    // 没有任何东西守着——重了的表现是同一条会话在左栏出现两次。
    const seen = new Set<string>()
    return rows.filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)))
  }, [conversations.data?.conversations, projectConversationData])
  const railGroups = useMemo(
    () =>
      groupRailConversations(
        list.map((c) => ({
          id: c.id,
          title: c.title,
          projectId: c.project_id ?? null,
          updatedAt: c.updated_at,
        })),
        railProjects.data?.items ?? [],
        t('lulu_workbench.rail.group_general'),
      ),
    [list, railProjects.data?.items],
  )

  // 路由没给会话就落到列表第一条。**列表还没回来的时候不落**——
  // 落一个 undefined 会让下面所有 query 以 enabled:false 空转一轮。
  const activeId = params.conversationId ?? list[0]?.id

  const projects = useProjects()
  const wb = useConversationWorkbench({
    conversationId: activeId,
    // **scope 用 account**：Lulu 首屏是总控话头，项目内那一版由 project 屏传自己的 scope 进来。
    agentScope: { scope: 'account' },
    // 名字在两处出现：标题栏（这条会话）与左栏（列表），两处都重取。
    onRenamed: () =>
      Promise.all([
        conversations.refetch(),
        ...projectConversations
          .filter((q) => q.data?.conversations.some((c) => c.id === activeId) ?? false)
          .map((q) => q.refetch()),
      ]),
    // **这一轮 Lulu 可能已经动过平台了，把项目列表重取一次。** 它有工具，
    // 其中一件（create_project）真的会往库里写一行，而项目列表的 staleTime 是 30 秒。
    // 重取的判据是「这一轮结束了」，不是「它说它建了」——猜一个模型写的句子是最不该做的事。
    onReplyArrived: () => void projects.refetch(),
  })
  const { conversation, stream, snapshot, attach, filePickerRef } = wb

  // 侧边栏的「有新消息」点（L-67，客户端版，见 unread.ts 顶上那段边界）。
  // 正看着的那条**随时**标为已看：自己刚发的那条也会推进 updated_at。
  const activeUpdatedAt = conversation.data?.conversation.updated_at
  useEffect(() => {
    if (activeId !== undefined && activeUpdatedAt !== undefined) markSeen(activeId, activeUpdatedAt)
  }, [activeId, activeUpdatedAt])
  const unread = useMemo(
    // **用 list，不用 conversations.data。** list 是账户会话 + 各项目会话
    // 合起来的那一份；只喂账户那份的话，侧栏里占大多数的项目会话永远不打点。
    () => unreadIds(list, activeId),
    [list, activeId, activeUpdatedAt],
  )

  // 这一份产物此刻跑在哪儿。算一次往下传——**不在多处各算一次**：
  // 两处算出不同结果的那天，Web 上就会冒出一个本地目录入口。
  const surface = detectSurface()

  const [folderVerdict, setFolderVerdict] = useState<FolderVerdict | null>(null)

  /**
   * 点了「选择文件夹」或「新建文件夹」。
   *
   * 三步：让原生那侧选并识别 → 把判定显示出来 → 能往下走才登记。
   *
   * **`mayProceed` 为假时绝不登记。** 已被别的项目占用的文件夹一旦
   * 登记上去，就是两个项目同时往一个目录写——那不是体验问题，
   * 是数据损坏。判定卡会显示原因和出口，但这一步到此为止。
   *
   * 没有原生那一侧（Web／移动端）时 `pickLocalFolder` 回 null，
   * 整个函数什么都不做——而那两个按钮本来也不渲染。
   */
  const onFolderAction = useCallback(
    async (create: boolean, projectId: string | null) => {
      const picked = await pickLocalFolder(surface, create)
      if (picked === null) return
      setFolderVerdict(picked.verdict)
      if (!mayProceed(picked.verdict)) return
      // 没选项目就只显示判定，**不登记**——挂到一个猜出来的项目上
      // 比不挂糟得多：那正是「静默抢占」的另一种长相。
      if (projectId === null) return
      await registerCheckout(
        buildCheckoutRegistration({
          projectId,
          deviceRef: picked.deviceRef,
          fingerprint: picked.fingerprint,
          path: picked.path,
        }),
      )
    },
    [surface],
  )

  const { measureRef, tracks, sidecarTab, openSidecar, closeSidecar, resizeSidecar } =
    useSidecarLayout()

  const selectConversation = useCallback(
    (id: string) => {
      void navigate({
        to: '/lulu/c/$conversationId',
        params: { conversationId: id },
        search: (prev: Record<string, unknown>) => prev,
      })
    },
    [navigate],
  )

  const openNewConversation = useCallback(() => {
    void navigate({ to: '/lulu/new' })
  }, [navigate])

  // ── 新会话启动器 ────────────────────────────────────────────
  //
  // 它与会话共用这一个组件而不是另开一屏：设计稿要求启动器是**主聊天区的
  // 一次切换**，不盖旧聊天、不占 Sidecar、无遮罩。
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const launching = pathname === '/lulu/new'

  const [keyword, setKeyword] = useState('')

  const recent = useMemo(
    () =>
      resolveRecentProjects({
        isLoading: projects.isPending,
        failed: Boolean(projects.error),
        // **取数失败时必须传 null，不是空数组。** 传空数组的话，
        // 一次读失败会被显示成「你还没有项目」——设计稿 Z10 说
        // 「写反了用户会以为项目丢了」。
        projects: projects.error
          ? null
          : (projects.data?.items ?? []).map((item) => ({
              id: item.id,
              name: item.name,
              // 后端的 Project 上没有同步状态字段，所以它就是没核验过。
              // 填 true 会是在替后端做一个它没给出的判断。
              synced: null,
            })),
        keyword,
      }),
    [projects.isPending, projects.error, projects.data?.items, keyword],
  )

  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState(false)

  /**
   * 真建一条会话。
   *
   * 上一版这里只 `navigate('/lulu')`，注释写着「后端只收 {title}」——
   * **那句话在合同落地那天变成了假话**，而它把整条新会话的路冻在了
   * 一次假的跳转上：界面看着响应了，库里什么都没多。
   *
   * 现在按 `mode`／`projectId` 走两条真路由，建好之后进 `/lulu/c/{id}`。
   * 失败**不跳转**——跳过去会得到一个不存在的会话，而用户会以为是
   * 它自己坏了。留在原地把话说清楚，重试就是再点一次。
   */
  const startConversation = useCallback(
    (mode: ConversationMode, projectId: string | null) => {
      setStartError(false)
      if (!creatorUserId || !canStartConversation) {
        setStartError(true)
        return
      }
      setStarting(true)
      const title = t('lulu_workbench.launch.new_title')
      const intent: ConversationCreateIntent = {
        creatorUserId,
        projectId,
        mode,
        title,
      }
      void (async () => {
        const key = conversationCreateKeys.keyFor(intent)
        try {
          const created = await createLuluConversation({
            title,
            projectId,
            mode,
            idempotencyKey: key,
          })
          releaseCreateConversationKey(conversationCreateKeys, intent, key, 201, true)
          void navigate({ to: '/lulu/c/$conversationId', params: { conversationId: created.id } })
        } catch (err) {
          const status = (err as { status?: number } | null)?.status
          releaseCreateConversationKey(conversationCreateKeys, intent, key, status, false)
          setStartError(true)
        } finally {
          setStarting(false)
        }
      })()
    },
    [canStartConversation, creatorUserId, navigate],
  )

  // ── 新建项目的三阶段（验收 #49 #50 #51 #53）────────────────────────
  //
  // 上一版这条路是「建完项目立刻开会话然后跳走」，三阶段那一屏在真实流程里
  // 根本没有出现的时机——台账把 #49/#50 标 `todo` 时写的就是这句话。
  //
  // 现在它按设计稿 Z11 走：建项目 → **在这一页等服务端逐格确认** →
  // 前置齐了才建会话 → 服务端说 ready 才进去。中途关页，记录在 localStorage 里，
  // 再进来接着看真实阶段（不从头播一遍）。

  const [provisioning, setProvisioning] = useState<ProvisioningRecord | null>(() =>
    readProvisioning(typeof localStorage === 'undefined' ? null : localStorage),
  )
  const provisioningStore = typeof localStorage === 'undefined' ? null : localStorage

  /**
   * 三阶段的真实状态。**这是 `useProjectInitStatus` 的第一个调用方**——
   * 在这之前它是一个只有定义、没有任何人调的函数。
   *
   * 没在建项目时 `projectId` 是 undefined，query 自己 `enabled: false`，
   * 一个请求都不发。
   *
   * 轮询：没到 ready 之前每 1.5 秒问一次。**GET 天然幂等**，所以「关页再进」
   * 不需要任何额外机制——重新拉一次就是当时的样子。
   */
  const initStatus = useProjectInitStatus(provisioning?.projectId)
  // **解构出这两个再用。** 直接在 effect 里写 `initStatus.x`，
  // exhaustive-deps 要的是整个 `initStatus` 对象，而它每次渲染都是新的引用
  // ——依赖数组里放它等于每渲染一次就重装一次定时器，那个循环
  // 不会报错，只会让这一页每秒发好几次请求。
  const initData = initStatus.data
  const refetchInit = initStatus.refetch
  useEffect(() => {
    if (!provisioning) return
    if (isReadableInitStatus(initData) && initData.ready) return
    const id = setInterval(() => void refetchInit(), 1500)
    return () => clearInterval(id)
  }, [provisioning, initData, refetchInit])

  /** 用这个名字新建项目。**建完不跳走**——留在这一页看三阶段。 */
  const createNamed = useCallback(
    async (name: string) => {
      if (!canCreateProject) return
      // 根钥匙：这一次「新建项目」从头到尾就这一把，三步各派生一个子键。
      // **生成一次就存住**——每次重试现生成一把，等于每次都建一个新项目，
      // 而那正是这把钥匙要挡的事。
      const rootKey = crypto.randomUUID()
      const created = await createProjectNamed(name, projectCreateKey(rootKey))
      const rec: ProvisioningRecord = {
        projectId: created.id,
        name,
        rootKey,
        conversationId: null,
      }
      writeProvisioning(provisioningStore, rec)
      setProvisioning(rec)
      await projects.refetch()
    },
    [canCreateProject, projects, provisioningStore],
  )

  /**
   * 三阶段的驱动。判据全部来自服务端那份响应，一条都不来自本地推断。
   *
   * `creatingRef` 挡住 React 严格模式下的双跑与轮询到来时的重入：
   * 会话那一步带幂等键，重复发不会建出第二条，**但也不该真的发第二次**——
   * 一条打不出去的请求和一条打出去被去重的请求，在网络面板里长得不一样，
   * 而下一个查这条路的人会去查那条多出来的请求。
   */
  const creatingConversationRef = useRef(false)
  useEffect(() => {
    if (!provisioning) return
    const step = nextProvisioningStep(initData, provisioning)
    if (step.kind === 'wait') return
    if (step.kind === 'blocked') {
      // 项目归档了：这条路走不下去了，把记录清掉别让它一直转。
      clearProvisioning(provisioningStore)
      setProvisioning(null)
      return
    }
    if (step.kind === 'enter') {
      clearProvisioning(provisioningStore)
      setProvisioning(null)
      void navigate({
        to: '/lulu/c/$conversationId',
        params: { conversationId: step.conversationId },
      })
      return
    }
    if (step.kind === 'enter_unknown') {
      // 会话是在另一个标签页/另一台设备上建的。去这个项目的会话列表里找。
      // 找不到就等下一轮——**不新建一条**，那会建出第二条会话。
      const idx = projectIds.indexOf(provisioning.projectId)
      const found = idx >= 0 ? projectConversations[idx]?.data?.conversations?.[0]?.id : undefined
      if (!found) return
      clearProvisioning(provisioningStore)
      setProvisioning(null)
      void navigate({ to: '/lulu/c/$conversationId', params: { conversationId: found } })
      return
    }
    // create_conversation：第 3 步。
    if (creatingConversationRef.current) return
    creatingConversationRef.current = true
    void (async () => {
      if (!creatorUserId) {
        creatingConversationRef.current = false
        return
      }
      const title = t('lulu_workbench.launch.new_title')
      const intent: ConversationCreateIntent = {
        creatorUserId,
        projectId: provisioning.projectId,
        mode: 'solo',
        title,
      }
      const key = conversationCreateKeys.keyFor(intent, () =>
        conversationCreateKey(provisioning.rootKey),
      )
      try {
        const conv = await createLuluConversation({
          title,
          projectId: provisioning.projectId,
          mode: 'solo',
          idempotencyKey: key,
        })
        releaseCreateConversationKey(conversationCreateKeys, intent, key, 201, true)
        const next = { ...provisioning, conversationId: conv.id }
        writeProvisioning(provisioningStore, next)
        setProvisioning(next)
        await refetchInit()
      } catch {
        // 会话没建成**不回滚项目**（验收 #52，设计稿 Z12：项目是资产，
        // 会话只是一次对话）。留在这一页，下一轮轮询会再试一次，
        // 用的还是同一把钥匙 —— 所以重试不会建出第二条。
        creatingConversationRef.current = false
      }
    })()
  }, [
    creatorUserId,
    provisioning,
    initData,
    refetchInit,
    projectIds,
    projectConversations,
    navigate,
    provisioningStore,
  ])

  /** 传给启动器那一屏的三阶段行。没在建项目就是 null，整块不渲染。 */
  const initStageRows = useMemo(() => {
    if (!provisioning) return null
    if (!isReadableInitStatus(initData)) return null
    return stageRows(fromWireInitStatus(initData))
  }, [provisioning, initData])
  const initReady = Boolean(provisioning && isReadableInitStatus(initData) && initData.ready)

  /**
   * 启动器那一栏。**不再提前 return 整个框。**
   *
   * 画板 Z01 顶上那句话是合同：「点「+ 新会话」后**主聊天区**换成启动页，
   * 不弹覆盖层、不盖旧聊天、不占右侧 Sidecar」——只换中间那一栏。
   *
   * 上一版是 `if (launching) return <LuluFrame>…`，把整个框都换掉了，
   * 于是左边 232px 的会话栏在启动器上整条消失。而这个洞很难被发现：
   * 代码是新的、缓存也没问题、启动器本身每一行都对得上画板，
   * **少的是一整栏**，只有把两张图并排看才看得出来。
   */
  const launcher = launching ? (
    // 启动器内容比一屏高是常态，所以它自己滚 —— 而不是让整页滚。
    <div
      data-testid="lulu-launcher-column"
      className="min-h-0 flex-1 overflow-y-auto bg-npc-lulu-bg-0"
    >
      <LaunchPage
        surface={surface}
        canStart={canStartConversation}
        canCreateProject={canCreateProject}
        initStages={initStageRows}
        initReady={initReady}
        onCreateNamed={createNamed}
        onFolderAction={onFolderAction}
        folderVerdict={folderVerdict}
        recent={recent}
        keyword={keyword}
        onKeywordChange={setKeyword}
        onStart={startConversation}
        onRetryProjects={() => void projects.refetch()}
        starting={starting}
        startFailed={startError}
        modePersistable={endpointStatus('POST /conversations') === 'wired'}
      />
    </div>
  ) : undefined

  return (
    <LuluFrame measureRef={measureRef}>
      <HiddenAttachmentInput inputRef={filePickerRef} onPick={attach.add} />
      {/*
        侧栏三态分开传，**不许合并**：
          undefined = 正在读（设计稿 Z10：读不到之前不放灰色骨架）
          null      = 读不到（侧栏说「暂时读不到 · {什么}」并给重试）
          []        = 真的没有
        团队是**工作板 session 的投影**，不是 Agent 库——两份数据都要读到才画。
      */}
      <LuluView
        // 真实过程步骤。这一行 2026-08-30 补上之前在这一层零消费方——
        // 下面整条链收到的永远是 undefined，屏幕上是一条空的「工作过程」横条。
        runSteps={stream.steps}
        launcher={launcher}
        snapshot={snapshot}
        unread={unread}
        team={wb.team}
        teamCandidates={wb.teamCandidates}
        // 一起管这条会话的真人。**与 team 同一份 board 快照**。
        people={wb.people}
        plan={wb.plan}
        board={wb.boardNodes}
        decisions={wb.pendingDecisions}
        bindTargets={
          projects.isError
            ? null
            : projects.data === undefined
              ? null
              : (projects.data.items ?? []).slice(0, 6).map((p) => ({ id: p.id, name: p.name }))
        }
        replyAskedForProject={wb.askedForProject}
        onBindProject={async (projectId) => {
          await wb.bindProject(projectId)
          // 绑完把列表也重取一次：左栏的分组是从服务端读的，不是本地改的。
          await conversations.refetch()
        }}
        invitablePeople={wb.invitablePeople}
        onInvitePerson={wb.invitePerson}
        onRemovePerson={wb.removePerson}
        onJoinTeam={wb.joinTeam}
        onRetrySidecar={wb.retrySidecar}
        goalPlan={wb.goalPlan.isPending ? undefined : (wb.goalPlan.data ?? null)}
        justScheduledTaskId={wb.justScheduledTaskId}
        attachments={attach.items}
        onPickAttachment={activeId === undefined ? undefined : () => filePickerRef.current?.click()}
        onRetryAttachment={attach.retry}
        onRemoveAttachment={attach.remove}
        attachmentNotice={attach.notice}
        onStartWork={activeId === undefined ? undefined : wb.startWork}
        startingWork={wb.startingWork}
        startWorkFailed={wb.startWorkFailed}
        composerModel={wb.composerModel.data?.composer_model ?? null}
        onSendMessage={activeId === undefined ? undefined : wb.sendMessage}
        onAnswerDecision={wb.answer}
        onRecoverSubrun={wb.recover}
        onUndoTask={wb.undoTask}
        onCancelSubrun={wb.cancelSubrun}
        onStopRound={activeId === undefined ? undefined : wb.stopRound}
        paused={wb.paused}
        onPause={activeId === undefined || wb.archived ? undefined : wb.pause}
        onResume={activeId === undefined || wb.archived ? undefined : wb.resume}
        skills={wb.skills.data?.skills ?? (wb.skills.isError ? null : undefined)}
        onRenameConversation={activeId === undefined ? undefined : wb.rename}
        models={wb.agentModelOptions}
        onPickModel={activeId === undefined ? undefined : wb.pickModel}
        railGroups={railGroups}
        thinkingSeconds={wb.thinkingSeconds}
        messages={wb.messages.data?.messages ?? []}
        participants={wb.participantsById}
        tracks={tracks}
        sidecarTab={sidecarTab}
        onOpenSidecar={openSidecar}
        onCloseSidecar={closeSidecar}
        onSelectConversation={selectConversation}
        onNewConversation={openNewConversation}
        onResizeSidecar={resizeSidecar}
        conversationNavigationExternal
      />
    </LuluFrame>
  )
}
